<template>
  <MobileScale :design-width="1920" :min-scale="0.6" :breakpoint="992">
    <TopLoading ref="topLoadingRef" />
    <!--
      最外层 router-view 不要再包 <KeepAlive>。
      原因：App.vue 下同时存在两个根路由（User、Layout/realName/livenessVerification），
      如果在 App.vue 处包 <KeepAlive>，路由在 User 和业务路由之间切换时，
      被缓存的顶层组件会随其后继组件重新挂载而重复创建（导致 onMounted 触发多次、接口被重复调用）。
      业务页面的 <KeepAlive> 已经在 Layout.vue 中按 route.meta.keepAlive 精确控制。
    -->
    <router-view />
  </MobileScale>

  <!-- 可拖拽悬浮客服按钮 -->
  <!-- <div
    class="float-service-btn"
    :class="{ dragging: isDragging }"
    :style="btnStyle"
    @mousedown="handleDragStart"
    @touchstart.prevent="handleTouchStart"
  >
    <el-icon :size="26" color="#ffffff">
      <Service />
    </el-icon>
  </div> -->
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElIcon } from 'element-plus'
import { Service } from '@element-plus/icons-vue'
import TopLoading from '@/components/common/TopLoading.vue'
import MobileScale from '@/components/common/MobileScale.vue'
import { useTabsStore } from '@/stores/modules/tabs'
import { getStorage, setStorage } from '@/utils/storage'
// 请替换为你项目中 getConfigApi 的实际引入路径
import { getConfigApi } from '@/api/modules/config'

const tabsStore = useTabsStore()
const topLoadingRef = ref<InstanceType<typeof TopLoading>>()

// ========== 客服链接配置 ==========
const SERVICE_BASE = 'https://cs.apimcapital.top/chat/index'
const serviceToken = ref('') // 配置接口返回的客服token
const BTN_SIZE = 56 // 按钮宽高，与样式保持一致

// 按钮定位样式
const btnStyle = reactive({
  right: '20px',
  bottom: '120px',
  left: '',
  top: '',
  transform: 'none'
})

const isDragging = ref(false)
let startX = 0
let startY = 0
let startTranslateX = 0
let startTranslateY = 0

// ========== 统一打开客服窗口逻辑 ==========
const openCustomerService = () => {
  const params = new URLSearchParams()
  // 基础固定参数
  params.append('noCanClose', '1')
  params.append('token', 'b220306eaec0726e8a235e9160911fa1')

  // 登录态用户信息拼接
  const userInfo = getStorage('userInfo') as any
  if (userInfo) {
    params.append('uid', userInfo.id)
    params.append('nickName', userInfo.username)
    params.append('phone', userInfo.mobile || userInfo.email || '')
  }

  const fullUrl = `${SERVICE_BASE}?${params.toString()}`
  window.open(fullUrl, '_blank')
}

// ========== PC端拖拽逻辑 ==========
const handleDragStart = (e: MouseEvent) => {
  const el = e.currentTarget as HTMLElement
  el.style.transition = 'none'

  isDragging.value = false
  startX = e.clientX
  startY = e.clientY

  const rect = el.getBoundingClientRect()
  startTranslateX = rect.left
  startTranslateY = rect.top

  btnStyle.right = ''
  btnStyle.bottom = ''
  btnStyle.left = '0'
  btnStyle.top = '0'
  btnStyle.transform = `translate(${startTranslateX}px, ${startTranslateY}px)`

  document.addEventListener('mousemove', handleDragMove)
  document.addEventListener('mouseup', handleDragEnd)
}

const handleDragMove = (e: MouseEvent) => {
  const moveX = e.clientX - startX
  const moveY = e.clientY - startY

  if (Math.abs(moveX) > 5 || Math.abs(moveY) > 5) {
    isDragging.value = true
  }

  let newX = startTranslateX + moveX
  let newY = startTranslateY + moveY
  const maxX = window.innerWidth - BTN_SIZE
  const maxY = window.innerHeight - BTN_SIZE
  newX = Math.max(0, Math.min(newX, maxX))
  newY = Math.max(0, Math.min(newY, maxY))

  btnStyle.transform = `translate(${newX}px, ${newY}px)`
}

const handleDragEnd = () => {
  const el = document.querySelector('.float-service-btn') as HTMLElement
  if (el) el.style.transition = ''

  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('mouseup', handleDragEnd)

  // 非拖拽状态触发跳转
  if (!isDragging.value) {
    openCustomerService()
  }
  isDragging.value = false
}

// ========== 移动端拖拽逻辑 ==========
const handleTouchStart = (e: TouchEvent) => {
  const el = e.currentTarget as HTMLElement
  el.style.transition = 'none'

  const touch = e.touches[0] as any
  isDragging.value = false
  startX = touch.clientX
  startY = touch.clientY

  const rect = el.getBoundingClientRect()
  startTranslateX = rect.left
  startTranslateY = rect.top

  btnStyle.right = ''
  btnStyle.bottom = ''
  btnStyle.left = '0'
  btnStyle.top = '0'
  btnStyle.transform = `translate(${startTranslateX}px, ${startTranslateY}px)`

  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)
}

const handleTouchMove = (e: TouchEvent) => {
  e.preventDefault()
  const touch = e.touches[0] as any
  const moveX = touch.clientX - startX
  const moveY = touch.clientY - startY

  if (Math.abs(moveX) > 5 || Math.abs(moveY) > 5) {
    isDragging.value = true
  }

  let newX = startTranslateX + moveX
  let newY = startTranslateY + moveY
  const maxX = window.innerWidth - BTN_SIZE
  const maxY = window.innerHeight - BTN_SIZE
  newX = Math.max(0, Math.min(newX, maxX))
  newY = Math.max(0, Math.min(newY, maxY))

  btnStyle.transform = `translate(${newX}px, ${newY}px)`
}

const handleTouchEnd = () => {
  const el = document.querySelector('.float-service-btn') as HTMLElement
  if (el) el.style.transition = ''

  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)

  if (!isDragging.value) {
    openCustomerService()
  }
  isDragging.value = false
}

// 组件卸载清除事件
onUnmounted(() => {
  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('mouseup', handleDragEnd)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
})

onMounted(async () => {
  // 页面初始化获取客服配置token
  try {
    const data = await getConfigApi()
    serviceToken.value = data?.ios?.upload_url || ''
  } catch (err) {
    console.error('获取客服配置失败：', err)
  }

  // 原有业务逻辑
  // @ts-ignore
  window.__topLoading__ = topLoadingRef.value
  tabsStore.validateAndRecoverTabs()
})
</script>

<style scoped lang="scss">
.float-service-btn {
  position: fixed;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff 0%, #1677ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  z-index: 9999;
  user-select: none;
  will-change: transform;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.35);
  transition: all 0.25s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(64, 158, 255, 0.45);
  }

  &:active {
    transform: scale(0.98);
  }

  &.dragging {
    cursor: grabbing;
  }
}
</style>